import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Camera, CheckCircle2, FileUp, Flashlight,
  Images, Loader2, QrCode, RotateCcw, ScanLine, ShieldCheck,
  Upload, X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { formatBytes } from '../../components/utils/time';
import { ROUTES } from '../../config/routes';
import { RUNTIME } from '../../config/runtime';
import { useAuthStore } from '../../stores/authStore';
import { useAuditStore } from '../../stores/auditStore';
import { useDepartmentsStore } from '../../stores/departmentsStore';
import { useDocumentsStore } from '../../stores/documentsStore';
import { useUIStore } from '../../stores/uiStore';
import { imagesToPdf, inferFileType, processFile, type ProcessedFile } from '../../services/compression';
import {
  AutoCaptureTracker, COLOR_MODES, frameDifference, processDocumentImage, terminateProcessor,
  type ColorMode, type RasterImage,
} from '../../services/scanner';
import { buildDocument } from '../../services/documents';
import { saveDocumentFile } from '../../services/storage';
import { beginDriveUpload, deleteDriveFile, endDriveUpload, uploadToDrive } from '../../services/drive';
import { apiRequest } from '../../services/api';
import type { Confidentiality } from '../../types';
import { DriveDestinationSelector } from '../departments/DriveDestinationSelector';

const DEFAULT_LIMIT = 512_000;
const EXCEPTION_LIMIT = 2_097_152;

type Step = 'choose' | 'camera' | 'processing' | 'review' | 'metadata' | 'qr-scan' | 'done';
type Source = 'camera' | 'upload' | 'multi_image';

interface MetadataState {
  nameAr: string;
  nameEn: string;
  bookNumber: string;
  departmentId: string;
  category: string;
  confidentiality: Confidentiality;
  documentDate: string;
  sender: string;
  recipient: string;
  notes: string;
  tags: string;
}

const initialMetadata = (departmentId = ''): MetadataState => ({
  nameAr: '',
  nameEn: '',
  bookNumber: '',
  departmentId,
  category: 'cat-books',
  confidentiality: 'internal',
  documentDate: new Date().toISOString().slice(0, 10),
  sender: '',
  recipient: '',
  notes: '',
  tags: '',
});

export const ScannerOverlay: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { scannerOpen, closeScanner, lang, showToast } = useUIStore();
  const { documents, addDocument } = useDocumentsStore();
  const { departments, adjustUsage } = useDepartmentsStore();
  const { user } = useAuthStore();
  const { add: addAudit } = useAuditStore();
  const isRTL = lang === 'ar';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const multiInputRef = useRef<HTMLInputElement>(null);
  const qrInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const qrControlsRef = useRef<{ stop: () => void } | null>(null);

  const [step, setStep] = useState<Step>('choose');
  const [processed, setProcessed] = useState<ProcessedFile | null>(null);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string>();
  const [source, setSource] = useState<Source>('upload');
  const [error, setError] = useState<string>('');
  const [exceptionApproved, setExceptionApproved] = useState(false);
  const [metadata, setMetadata] = useState<MetadataState>(() => initialMetadata(departments[0]?.id));
  const [savedDocumentId, setSavedDocumentId] = useState<string>('');
  const [torchOn, setTorchOn] = useState(false);
  const [colorMode, setColorMode] = useState<ColorMode>('color');
  const [enhance, setEnhance] = useState(true);
  const [autoCapture, setAutoCapture] = useState(false);
  const [steady, setSteady] = useState(false);
  const trackerRef = useRef<AutoCaptureTracker | null>(null);
  const captureLockRef = useRef(false);

  const selectedDepartment = useMemo(
    () => departments.find(department => department.id === metadata.departmentId),
    [departments, metadata.departmentId],
  );

  useEffect(() => {
    if (!scannerOpen) return;
    const permitted = user?.departments?.[0];
    const defaultId = user?.role === 'system_admin'
      ? departments.find(department => department.status === 'active')?.id
      : departments.find(department => department.id === permitted && department.status === 'active')?.id;
    setMetadata(current => ({ ...current, departmentId: current.departmentId || defaultId || '' }));
  }, [scannerOpen, departments, user]);

  useEffect(() => () => {
    stopCamera();
    terminateProcessor(); // release the processing Web Worker — prevents memory leaks
    if (processed?.previewUrl) URL.revokeObjectURL(processed.previewUrl);
  }, [processed?.previewUrl]);

  // Frame-stability tracking for steadiness feedback and optional auto-capture.
  useEffect(() => {
    if (step !== 'camera') return;
    trackerRef.current = new AutoCaptureTracker(0.02, 8);
    let prev: RasterImage | null = null;
    let raf = 0;
    const sample = document.createElement('canvas');
    sample.width = 120; sample.height = 90;
    const sctx = sample.getContext('2d', { willReadFrequently: true });
    const tick = () => {
      const video = videoRef.current;
      if (video && video.videoWidth && sctx) {
        sctx.drawImage(video, 0, 0, sample.width, sample.height);
        const frame: RasterImage = { data: sctx.getImageData(0, 0, sample.width, sample.height).data, width: sample.width, height: sample.height };
        if (prev) {
          const diff = frameDifference(prev, frame);
          setSteady(diff <= 0.02);
          if (trackerRef.current?.push(diff) && autoCapture) { void capturePhoto(); return; }
        }
        prev = frame;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, autoCapture]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    setTorchOn(false);
    qrControlsRef.current?.stop();
    qrControlsRef.current = null;
  };

  const reset = () => {
    stopCamera();
    if (processed?.previewUrl) URL.revokeObjectURL(processed.previewUrl);
    setStep('choose');
    setProcessed(null);
    setSource('upload');
    setError('');
    setExceptionApproved(false);
    setSavedDocumentId('');
    setMetadata(initialMetadata(departments[0]?.id));
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (multiInputRef.current) multiInputRef.current.value = '';
    if (qrInputRef.current) qrInputRef.current.value = '';
  };

  const close = () => {
    closeScanner();
    reset();
  };

  const startDocumentCamera = async () => {
    setError('');
    setStep('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setError(isRTL ? 'تعذر فتح الكاميرا. يمكنك اختيار صورة أو ملف من الهاتف.' : 'Camera unavailable. Choose an image or file instead.');
    }
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
    if (!capabilities.torch) {
      showToast(isRTL ? 'الفلاش غير مدعوم على هذا الجهاز أو المتصفح' : 'Torch is not supported by this device or browser', 'info');
      return;
    }
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next } as unknown as MediaTrackConstraintSet] });
      setTorchOn(next);
    } catch {
      showToast(isRTL ? 'تعذر تغيير حالة الفلاش' : 'Could not change torch state', 'error');
    }
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || captureLockRef.current) return;
    captureLockRef.current = true;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) { captureLockRef.current = false; return; }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Professional pipeline: edge detection → perspective correction → shadow/contrast enhancement
    // → color mode. Heavy work runs in a Web Worker (main-thread fallback) to keep the UI smooth.
    let outCanvas = canvas;
    if (enhance) {
      try {
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const raster: RasterImage = { data: imageData.data, width: canvas.width, height: canvas.height };
        const processed = await processDocumentImage(raster, colorMode);
        outCanvas = document.createElement('canvas');
        outCanvas.width = processed.width; outCanvas.height = processed.height;
        outCanvas.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(processed.data), processed.width, processed.height), 0, 0);
      } catch { outCanvas = canvas; }
    }
    const blob = await new Promise<Blob | null>(resolve => outCanvas.toBlob(resolve, 'image/jpeg', 0.94));
    if (!blob) { captureLockRef.current = false; return; }
    stopCamera();
    const file = new File([blob], `camera-scan-${Date.now()}.jpg`, { type: 'image/jpeg' });
    setSource('camera');
    captureLockRef.current = false;
    await prepareFile(file);
  };

  const prepareFile = async (file: File) => {
    setError('');
    setStep('processing');
    setExceptionApproved(false);
    try {
      const dept = departments.find(item => item.id === metadata.departmentId) ?? departments[0];
      const result = await processFile(file, {
        targetBytes: dept?.quota.defaultMaxBytes ?? DEFAULT_LIMIT,
        exceptionBytes: dept?.quota.exceptionMaxBytes ?? EXCEPTION_LIMIT,
        knownHashes: documents.map(document => document.contentHash),
      });
      setProcessed(result);
      setMetadata(current => ({
        ...current,
        nameAr: current.nameAr || file.name.replace(/\.[^.]+$/, ''),
        departmentId: current.departmentId || dept?.id || '',
        category: current.category || dept?.categories[0]?.id || 'cat-books',
      }));
      setStep('review');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : (isRTL ? 'تعذرت معالجة الملف' : 'File processing failed'));
      setStep('choose');
    }
  };

  const handleSingleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSource('upload');
    await prepareFile(file);
  };

  const handleMultipleImages = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;
    setSource('multi_image');
    setStep('processing');
    setError('');
    try {
      const dept = departments.find(item => item.id === metadata.departmentId) ?? departments[0];
      const pdf = await imagesToPdf(files, dept?.quota.exceptionMaxBytes ?? EXCEPTION_LIMIT);
      await prepareFile(pdf);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : (isRTL ? 'تعذر دمج الصور' : 'Could not merge images'));
      setStep('choose');
    }
  };

  const startQrScanner = async () => {
    setStep('qr-scan');
    setError('');
    try {
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      const reader = new BrowserQRCodeReader();
      const controls = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } }, audio: false },
        videoRef.current ?? undefined,
        result => {
          if (!result) return;
          controls.stop();
          resolveQrText(result.getText());
        },
      );
      qrControlsRef.current = controls;
    } catch {
      setError(isRTL ? 'تعذر تشغيل قارئ QR. استخدم اختيار صورة QR.' : 'QR camera unavailable. Upload a QR image instead.');
    }
  };

  const resolveQrText = (text: string) => {
    try {
      const url = new URL(text, window.location.origin);
      const match = url.pathname.match(/^\/q\/([^/]+)$/);
      if (match) {
        closeScanner();
        reset();
        navigate(ROUTES.qr(match[1]));
        return;
      }
    } catch {
      // Continue to token fallback.
    }
    if (/^[A-Za-z0-9_-]{8,}$/.test(text)) {
      closeScanner();
      reset();
      navigate(ROUTES.qr(text));
      return;
    }
    setError(isRTL ? 'رمز QR لا يعود إلى هذا النظام.' : 'This QR code does not belong to this system.');
  };

  const handleQrImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      const reader = new BrowserQRCodeReader();
      const url = URL.createObjectURL(file);
      try {
        const result = await reader.decodeFromImageUrl(url);
        resolveQrText(result.getText());
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      setError(isRTL ? 'لم يتم العثور على QR صالح في الصورة.' : 'No valid QR code was found in the image.');
    }
  };

  const save = async () => {
    if (!processed || !user || !selectedDepartment) return;
    if (!metadata.nameAr.trim()) {
      setError(isRTL ? 'اسم المستند مطلوب.' : 'Document name is required.');
      return;
    }
    if (processed.result.isDuplicate) {
      setError(isRTL ? 'هذا الملف مكرر وموجود مسبقاً.' : 'This file is a duplicate.');
      return;
    }
    if (processed.result.exceptionRequired) {
      setError(isRTL ? 'يتجاوز الملف الحد الاستثنائي 2 MB ولا يمكن رفعه.' : 'File exceeds the 2 MB exception limit.');
      return;
    }
    if (processed.result.exceededLimit && !exceptionApproved) {
      setError(isRTL ? 'يتطلب الملف موافقة الاستثناء قبل الحفظ.' : 'Exception approval is required before saving.');
      return;
    }

    setError('');
    let driveFileId: string | undefined;
    let uploadedConnectorId: string | undefined;
    let uploadedFolderId: string | undefined;
    let uploadedStoragePolicy: import('../../types').StoragePolicyMode | undefined;
    try {
      let driveWebViewUrl: string | undefined;
      if (RUNTIME.mode === 'cloud') {
        beginDriveUpload(selectedDepartment.id);
        try {
          const uploaded = await uploadToDrive(
            selectedDepartment.id,
            processed.file,
            processed.file.name,
            processed.file.type || 'application/octet-stream',
            undefined,
            { connectorId: selectedConnectorId, category: metadata.category },
          );
          driveFileId = uploaded.fileId;
          uploadedConnectorId = uploaded.connectorId;
          uploadedFolderId = uploaded.folderId;
          uploadedStoragePolicy = uploaded.storagePolicy;
          driveWebViewUrl = uploaded.webViewLink;
        } finally {
          endDriveUpload(selectedDepartment.id);
        }
      }

      const { document, qr } = buildDocument({
        department: selectedDepartment,
        existingDocuments: documents,
        userId: user.id,
        nameAr: metadata.nameAr,
        nameEn: metadata.nameEn,
        bookNumber: metadata.bookNumber,
        category: metadata.category || selectedDepartment.categories[0]?.id || 'cat-books',
        confidentiality: metadata.confidentiality,
        fileType: inferFileType(processed.file),
        fileName: processed.file.name,
        mimeType: processed.file.type || 'application/octet-stream',
        originalBytes: processed.result.originalBytes,
        compressedBytes: processed.result.compressedBytes,
        pageCount: processed.result.pageCount,
        contentHash: processed.result.contentHash,
        driveFileId,
        driveConnectorId: uploadedConnectorId,
        providerFolderId: uploadedFolderId,
        storagePolicyUsed: uploadedStoragePolicy,
        driveWebViewUrl,
        source,
        documentDate: metadata.documentDate,
        sender: metadata.sender,
        recipient: metadata.recipient,
        notes: metadata.notes,
        tags: metadata.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        exceptionApproved: processed.result.exceededLimit ? exceptionApproved : undefined,
        exceptionReason: processed.result.exceededLimit ? 'Approved by authorized user' : undefined,
      });

      if (RUNTIME.mode === 'cloud') {
        await apiRequest('/api/documents', {
          method: 'POST',
          body: JSON.stringify({ ...document, qrId: qr.id, qrToken: qr.token }),
        });
      } else {
        await saveDocumentFile(document.id, processed.file, processed.file.name, processed.file.type);
      }
      addDocument(document, qr);
      adjustUsage(selectedDepartment.id, processed.file.size, 1);
      addAudit({
        userId: user.id,
        userNameAr: user.nameAr,
        action: 'document.upload',
        targetId: document.id,
        targetType: 'document',
        departmentId: selectedDepartment.id,
        metadata: { archiveId: document.archiveId, fileName: document.fileName },
      });
      setSavedDocumentId(document.id);
      setStep('done');
      showToast(isRTL ? (RUNTIME.mode === 'cloud' ? 'تم الرفع إلى Google Drive وحفظ الفهرس وإنشاء QR ثابت' : 'تم حفظ المستند محلياً وإنشاء QR ثابت') : (RUNTIME.mode === 'cloud' ? 'Uploaded to Google Drive, indexed, and stable QR generated' : 'Document saved locally and stable QR generated'), 'success');
    } catch (caught) {
      if (RUNTIME.mode === 'cloud' && driveFileId) await deleteDriveFile(selectedDepartment.id, driveFileId, uploadedConnectorId).catch(() => undefined);
      setError(caught instanceof Error ? caught.message : (isRTL ? 'فشل حفظ المستند' : 'Document save failed'));
    }
  };

  const goToSavedDocument = () => {
    if (!savedDocumentId) return;
    closeScanner();
    reset();
    navigate(ROUTES.document(savedDocumentId));
  };

  const activeDepartments = departments.filter(department => department.status === 'active' && (
    user?.role === 'system_admin' || user?.departments.includes(department.id)
  ));

  return (
    <AnimatePresence>
      {scannerOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col scanner-surface"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className={`flex items-center justify-between px-4 py-4 safe-top ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <ScanLine size={21} className="text-gold-300" />
              <div className={isRTL ? 'text-right' : ''}>
                <h2 className="text-base font-bold text-champagne-100">{t('scanner.title')}</h2>
                <p className="text-[10px] text-champagne-500">MASAR Document Intelligence</p>
              </div>
            </div>
            <button type="button" onClick={close} aria-label={t('common.close')} className="icon-control glass-card">
              <X size={19} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-8">
            {error && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`mb-4 p-3 rounded-xl border border-red-500/30 bg-red-950/30 text-red-200 text-xs flex gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <AlertTriangle size={16} className="shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            {step === 'choose' && (
              <motion.div className="max-w-xl mx-auto pt-4 space-y-5" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <div className="scanner-orb mx-auto" aria-hidden="true"><ScanLine size={38} /></div>
                <div className="text-center">
                  <h3 className="text-lg font-black gold-text">{isRTL ? 'اختر طريقة إدخال المستند' : 'Choose document input'}</h3>
                  <p className="text-xs text-champagne-500 mt-1">{isRTL ? 'ضغط وفحص وتوليد QR تلقائياً' : 'Automatic compression, validation and QR generation'}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <ActionCard primary icon={<Camera size={25} />} label={isRTL ? 'تصوير بالكاميرا' : 'Camera scan'} onClick={startDocumentCamera} />
                  <ActionCard icon={<FileUp size={25} />} label={isRTL ? 'اختيار ملف' : 'Choose file'} onClick={() => fileInputRef.current?.click()} />
                  <ActionCard icon={<Images size={25} />} label={isRTL ? 'صور متعددة إلى PDF' : 'Images to PDF'} onClick={() => multiInputRef.current?.click()} />
                  <ActionCard icon={<QrCode size={25} />} label={isRTL ? 'مسح رمز QR' : 'Scan QR'} onClick={startQrScanner} />
                </div>
                <div className="glass-card rounded-2xl p-3 text-center text-[11px] text-champagne-500">
                  {isRTL ? 'الحد الاعتيادي 500 KB — الاستثناء حتى 2 MB — الملفات الموقعة لا تعدّل' : 'Default 500 KB — exception up to 2 MB — signed files are not modified'}
                </div>
              </motion.div>
            )}

            {step === 'camera' && (
              <motion.div className="max-w-xl mx-auto space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="relative overflow-hidden rounded-3xl border border-gold-500/30 bg-black aspect-[3/4] max-h-[65dvh]">
                  <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
                  <div className="absolute inset-5 scanner-frame">
                    <ScannerCorners steady={steady} />
                  </div>
                  <div className="absolute top-4 left-4 right-4 flex justify-between text-gold-200">
                    <button type="button" onClick={toggleTorch} aria-pressed={torchOn} aria-label={isRTL ? 'تبديل الفلاش' : 'Toggle torch'} className={`icon-control ${torchOn ? 'bg-gold-500/40 text-gold-100' : 'bg-black/40'}`}><Flashlight size={18} /></button>
                    <span className={`px-2 py-1 rounded-lg text-[11px] ${steady ? 'bg-emerald-500/30 text-emerald-100' : 'bg-black/40 text-gold-200'}`}>{steady ? (isRTL ? 'ثابت — جاهز' : 'Steady — ready') : (isRTL ? 'ثبّت الكاميرا' : 'Hold steady')}</span>
                    <button type="button" onClick={() => { stopCamera(); setStep('choose'); }} className="p-2 rounded-xl bg-black/40"><RotateCcw size={18} /></button>
                  </div>
                </div>
                <div className={`flex flex-wrap items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {COLOR_MODES.map(mode => <button key={mode} type="button" onClick={() => setColorMode(mode)} className={`px-3 py-1.5 rounded-full text-xs border ${colorMode === mode ? 'bg-gold-500/25 border-gold-400/50 text-gold-100' : 'bg-white/5 border-white/10 text-champagne-300'}`}>{mode === 'color' ? (isRTL ? 'ملون' : 'Color') : mode === 'grayscale' ? (isRTL ? 'رمادي' : 'Gray') : (isRTL ? 'أبيض/أسود' : 'B&W')}</button>)}
                  <label className="flex items-center gap-1.5 text-xs text-champagne-300"><input type="checkbox" checked={enhance} onChange={e => setEnhance(e.target.checked)} />{isRTL ? 'تحسين وتصحيح' : 'Enhance'}</label>
                  <label className="flex items-center gap-1.5 text-xs text-champagne-300"><input type="checkbox" checked={autoCapture} onChange={e => setAutoCapture(e.target.checked)} />{isRTL ? 'التقاط تلقائي' : 'Auto'}</label>
                </div>
                <button type="button" onClick={capturePhoto} className="camera-shutter mx-auto" aria-label={isRTL ? 'التقاط' : 'Capture'}><span /></button>
              </motion.div>
            )}

            {step === 'processing' && (
              <div className="min-h-[55dvh] flex flex-col items-center justify-center gap-5">
                <div className="scanner-orb"><Loader2 size={35} className="animate-spin" /></div>
                <div className="text-center">
                  <h3 className="font-bold text-champagne-100">{isRTL ? 'جارٍ تحليل المستند وضغطه' : 'Analyzing and compressing document'}</h3>
                  <p className="text-xs text-champagne-500 mt-1">{isRTL ? 'فحص النوع والبصمة والحجم والجودة' : 'Checking type, hash, size and quality'}</p>
                </div>
              </div>
            )}

            {step === 'review' && processed && (
              <motion.div className="scan-review max-w-xl mx-auto" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                {processed.previewUrl && <img src={processed.previewUrl} alt="Document preview" className="scan-review-preview w-full object-contain rounded-2xl bg-black/20 border border-white/10" />}
                <div className="scan-review-card glass-card rounded-2xl p-4 space-y-4">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Metric label={isRTL ? 'الأصلي' : 'Original'} value={formatBytes(processed.result.originalBytes)} />
                    <Metric label={isRTL ? 'النهائي' : 'Final'} value={formatBytes(processed.result.compressedBytes)} />
                    <Metric label={isRTL ? 'التخفيض' : 'Saved'} value={`${Math.max(0, Math.round((1 - processed.result.ratio) * 100))}%`} />
                  </div>
                  <div className="space-y-2 text-xs">
                    <StatusRow ok={!processed.result.isDuplicate} label={isRTL ? 'فحص التكرار' : 'Duplicate check'} value={processed.result.isDuplicate ? (isRTL ? 'مكرر' : 'Duplicate') : (isRTL ? 'فريد' : 'Unique')} />
                    <StatusRow ok={!processed.result.isDigitallySigned} label={isRTL ? 'التوقيع الرقمي' : 'Digital signature'} value={processed.result.isDigitallySigned ? (isRTL ? 'محمي ولم يُعدّل' : 'Protected; unchanged') : (isRTL ? 'غير موقع' : 'Not signed')} />
                    <StatusRow ok={!processed.result.exceptionRequired} label={isRTL ? 'سياسة الحجم' : 'Size policy'} value={processed.result.exceptionRequired ? (isRTL ? 'مرفوض: أكبر من 2 MB' : 'Rejected: over 2 MB') : processed.result.exceededLimit ? (isRTL ? 'يحتاج استثناء' : 'Needs exception') : (isRTL ? 'مطابق' : 'Compliant')} />
                  </div>
                  {processed.result.errorMessage && <p className="text-xs text-amber-200 bg-amber-950/30 border border-amber-500/20 rounded-xl p-3">{processed.result.errorMessage}</p>}
                  {processed.result.exceededLimit && !processed.result.exceptionRequired && (
                    <label className={`flex items-start gap-2 p-3 rounded-xl border border-gold-500/20 bg-gold-500/5 cursor-pointer ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                      <input type="checkbox" checked={exceptionApproved} onChange={event => setExceptionApproved(event.target.checked)} className="mt-0.5 accent-amber-500" />
                      <span className="text-xs text-champagne-300">{isRTL ? 'أوافق على رفعه كاستثناء لأنه يتجاوز 500 KB ولا يتجاوز 2 MB.' : 'Approve as an exception because it exceeds 500 KB but remains under 2 MB.'}</span>
                    </label>
                  )}
                  <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Button variant="ghost" fullWidth onClick={() => { setProcessed(null); setStep('choose'); }}>{isRTL ? 'اختيار غيره' : 'Choose another'}</Button>
                    <Button variant="gold" fullWidth disabled={processed.result.isDuplicate || processed.result.exceptionRequired} onClick={() => setStep('metadata')}>{isRTL ? 'متابعة' : 'Continue'}</Button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 'metadata' && processed && (
              <motion.div className={`max-w-xl mx-auto space-y-4 ${isRTL ? '[direction:rtl]' : ''}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <div>
                  <h3 className="text-lg font-black gold-text">{isRTL ? 'بيانات المستند' : 'Document metadata'}</h3>
                  <p className="text-xs text-champagne-500">{isRTL ? 'الحقول الأساسية مطلوبة للفهرسة والبحث' : 'Core fields are required for indexing and search'}</p>
                </div>
                <div className="glass-card rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={isRTL ? 'اسم المستند *' : 'Document name *'} value={metadata.nameAr} onChange={value => setMetadata({ ...metadata, nameAr: value })} />
                  <Field label="English name" value={metadata.nameEn} onChange={value => setMetadata({ ...metadata, nameEn: value })} dir="ltr" />
                  <Field label={isRTL ? 'رقم الكتاب' : 'Book number'} value={metadata.bookNumber} onChange={value => setMetadata({ ...metadata, bookNumber: value })} dir="ltr" />
                  <Field type="date" label={isRTL ? 'تاريخ المستند' : 'Document date'} value={metadata.documentDate} onChange={value => setMetadata({ ...metadata, documentDate: value })} dir="ltr" />
                  <SelectField label={isRTL ? 'الشعبة *' : 'Department *'} value={metadata.departmentId} onChange={value => setMetadata({ ...metadata, departmentId: value, category: departments.find(d => d.id === value)?.categories[0]?.id ?? 'cat-books' })} options={activeDepartments.map(department => ({ value: department.id, label: isRTL ? department.nameAr : department.nameEn }))} />
                  <SelectField label={isRTL ? 'التصنيف' : 'Category'} value={metadata.category} onChange={value => setMetadata({ ...metadata, category: value })} options={(selectedDepartment?.categories ?? []).map(category => ({ value: category.id, label: isRTL ? category.nameAr : category.nameEn }))} />
                  <SelectField label={isRTL ? 'السرية' : 'Confidentiality'} value={metadata.confidentiality} onChange={value => setMetadata({ ...metadata, confidentiality: value as Confidentiality })} options={[
                    { value: 'public', label: isRTL ? 'عام' : 'Public' },
                    { value: 'internal', label: isRTL ? 'داخلي' : 'Internal' },
                    { value: 'confidential', label: isRTL ? 'سري' : 'Confidential' },
                    { value: 'secret', label: isRTL ? 'سري جداً' : 'Secret' },
                  ]} />
                  <Field label={isRTL ? 'الجهة الصادرة' : 'Sender'} value={metadata.sender} onChange={value => setMetadata({ ...metadata, sender: value })} />
                  <Field label={isRTL ? 'الجهة المستلمة' : 'Recipient'} value={metadata.recipient} onChange={value => setMetadata({ ...metadata, recipient: value })} />
                  <Field label={isRTL ? 'الكلمات المفتاحية (بفواصل)' : 'Tags (comma separated)'} value={metadata.tags} onChange={value => setMetadata({ ...metadata, tags: value })} />
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-champagne-400 mb-1">{isRTL ? 'ملاحظات' : 'Notes'}</label>
                    <textarea rows={3} value={metadata.notes} onChange={event => setMetadata({ ...metadata, notes: event.target.value })} className="form-control resize-none" />
                  </div>
                </div>
                {selectedDepartment && processed && <DriveDestinationSelector departmentId={selectedDepartment.id} requiredBytes={processed.file.size} category={metadata.category} isRTL={isRTL} value={selectedConnectorId} onChange={setSelectedConnectorId} />}
                <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Button variant="ghost" fullWidth onClick={() => setStep('review')}>{isRTL ? 'رجوع' : 'Back'}</Button>
                  <Button variant="gold" fullWidth icon={<Upload size={16} />} onClick={save}>{isRTL ? 'حفظ وإنشاء QR' : 'Save & generate QR'}</Button>
                </div>
              </motion.div>
            )}

            {step === 'qr-scan' && (
              <motion.div className="max-w-xl mx-auto space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="relative overflow-hidden rounded-3xl bg-black aspect-square border border-gold-500/30">
                  <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
                  <div className="absolute inset-10 scanner-frame">
                    <ScannerCorners />
                  </div>
                </div>
                <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Button variant="ghost" fullWidth onClick={() => { stopCamera(); setStep('choose'); }}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
                  <Button variant="outline" fullWidth onClick={() => qrInputRef.current?.click()}>{isRTL ? 'اختيار صورة QR' : 'Choose QR image'}</Button>
                </div>
              </motion.div>
            )}

            {step === 'done' && (
              <motion.div className="min-h-[60dvh] flex flex-col items-center justify-center text-center gap-5" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
                <div className="success-medallion"><CheckCircle2 size={50} /></div>
                <div>
                  <h3 className="text-xl font-black gold-text">{isRTL ? 'اكتملت الأرشفة بنجاح' : 'Archive completed successfully'}</h3>
                  <p className="text-sm text-champagne-400 mt-1">{isRTL ? 'تم حفظ الملف محلياً وإنشاء QR ثابت وسجل تدقيق.' : 'File saved locally with stable QR and audit record.'}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={close}>{isRTL ? 'إغلاق' : 'Close'}</Button>
                  <Button variant="gold" icon={<QrCode size={16} />} onClick={goToSavedDocument}>{isRTL ? 'عرض المستند وQR' : 'View document & QR'}</Button>
                </div>
              </motion.div>
            )}
          </div>

          <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" onChange={handleSingleFile} />
          <input ref={multiInputRef} type="file" className="hidden" accept="image/*" multiple onChange={handleMultipleImages} />
          <input ref={qrInputRef} type="file" className="hidden" accept="image/*" onChange={handleQrImage} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ActionCard: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void; primary?: boolean }> = ({ icon, label, onClick, primary }) => (
  <motion.button type="button" whileTap={{ scale: 0.96 }} onClick={onClick} className={`scanner-action ${primary ? 'scanner-action-primary' : ''}`}>
    {icon}
    <span>{label}</span>
  </motion.button>
);

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl bg-white/5 border border-white/5 p-2">
    <p className="text-base font-black text-champagne-100">{value}</p>
    <p className="text-[10px] text-champagne-500">{label}</p>
  </div>
);

const StatusRow: React.FC<{ label: string; value: string; ok: boolean }> = ({ label, value, ok }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-champagne-400">{label}</span>
    <span className={`flex items-center gap-1 ${ok ? 'text-emerald-300' : 'text-amber-300'}`}>
      {ok ? <ShieldCheck size={13} /> : <AlertTriangle size={13} />}{value}
    </span>
  </div>
);

const Field: React.FC<{ label: string; value: string; onChange: (value: string) => void; type?: string; dir?: 'ltr' | 'rtl' }> = ({ label, value, onChange, type = 'text', dir }) => (
  <label className="block">
    <span className="block text-xs text-champagne-400 mb-1">{label}</span>
    <input type={type} value={value} onChange={event => onChange(event.target.value)} dir={dir} className="form-control" />
  </label>
);

const SelectField: React.FC<{ label: string; value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }> }> = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="block text-xs text-champagne-400 mb-1">{label}</span>
    <select value={value} onChange={event => onChange(event.target.value)} className="form-control">
      {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
  </label>
);

const ScannerCorners: React.FC<{ steady?: boolean }> = ({ steady }) => {
  const color = steady ? 'border-emerald-400/90' : 'border-gold-300/85';
  const corner = `absolute w-8 h-8 border-[3px] ${color} transition-colors`;
  return (
    <>
      <span className={`${corner} top-0 left-0 border-r-0 border-b-0 rounded-tl-xl`} />
      <span className={`${corner} top-0 right-0 border-l-0 border-b-0 rounded-tr-xl`} />
      <span className={`${corner} bottom-0 left-0 border-r-0 border-t-0 rounded-bl-xl`} />
      <span className={`${corner} bottom-0 right-0 border-l-0 border-t-0 rounded-br-xl`} />
    </>
  );
};
